import React, { FC, useState, useEffect } from "react";
import { Card, Row, Col, Statistic, Spin, Alert, Divider, Tag, Typography, Tooltip, Select, Space } from "antd";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Cell,
  FunnelChart,
  Funnel,
  LabelList,
  Tooltip as RechartsTooltip,
} from "recharts";
import { fetchFunnelData } from "../actions/overviewActions";
import { FunnelData } from "../types/overview";
import { COLORS } from "../utils/color";
import { useFilters } from "../hooks/useFilters";
import AnalyticsFilters from "./AnalyticsFilters";

const { Title, Text } = Typography;
const { Option } = Select;

type FunnelEndStep = "credit_card" | "watched_movie";

const FunnelAnalysis: FC = () => {
  const [loading, setLoading] = useState(true);
  const [funnelData, setFunnelData] = useState<FunnelData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedEndStep, setSelectedEndStep] = useState<FunnelEndStep>("credit_card");

  const { filters, filterOptions, handleDateRangeChange, handleFilterChange } = useFilters();

  useEffect(() => {
    loadFunnelData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEndStep, filters]);

  const loadFunnelData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchFunnelData(selectedEndStep, filters);
      setFunnelData(data);
    } catch (err) {
      setError("Failed to load funnel data");
      console.error("Funnel data loading error:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (minutes: number): string => {
    if (minutes === 0) return "0m";
    if (minutes < 1) return `${Math.round(minutes * 60)}s`;
    if (minutes < 60) return `${Math.round(minutes)}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = Math.round(minutes % 60);
    return `${hours}h ${remainingMinutes}m`;
  };

  const CustomFunnelTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div
          style={{
            backgroundColor: "white",
            border: "1px solid #ccc",
            borderRadius: "4px",
            padding: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <p style={{ margin: "0 0 4px 0", fontWeight: "bold" }}>{data.name}</p>
          <p style={{ margin: "0 0 4px 0", color: "#1890ff" }}>Users: {data.value.toLocaleString()}</p>
          <p style={{ margin: "0", color: "#52c41a" }}>Conversion Rate: {data.conversion_rate.toFixed(2)}%</p>
        </div>
      );
    }
    return null;
  };

  const CustomBarTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div
          style={{
            backgroundColor: "white",
            border: "1px solid #ccc",
            borderRadius: "4px",
            padding: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <p style={{ margin: "0 0 4px 0", fontWeight: "bold" }}>{label}</p>
          <p style={{ margin: "0 0 4px 0", color: "#1890ff" }}>Users Converted: {data.conversions.toLocaleString()}</p>
          <p style={{ margin: "0", color: "#52c41a" }}>Conversion Rate: {data.rate.toFixed(2)}%</p>
        </div>
      );
    }
    return null;
  };

  const getFunnelChartData = () => {
    if (!funnelData) return [];
    return funnelData.steps.map((step, index) => ({
      name: step.step.replace("user ", ""),
      value: step.users_converted,
      conversion_rate: step.conversion_rate,
      fill: COLORS[index % COLORS.length],
    }));
  };

  const getConversionBarData = () => {
    if (!funnelData) return [];
    return funnelData.steps.map((step, index) => ({
      step: step.step.replace("user ", "").replace(" page", ""),
      conversions: step.users_converted,
      rate: step.conversion_rate,
      fill: COLORS[index % COLORS.length],
    }));
  };

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: "center", padding: "50px 0" }}>
          <Spin size="large" />
          <p>Loading funnel analysis...</p>
        </div>
      </Card>
    );
  }

  if (error || !funnelData) {
    return (
      <Card>
        <Alert message="Error" description={error || "No funnel data available"} type="error" showIcon />
      </Card>
    );
  }

  return (
    <div>
      <AnalyticsFilters
        filters={filters}
        filterOptions={filterOptions}
        onDateRangeChange={handleDateRangeChange}
        onFilterChange={handleFilterChange}
        loading={loading}
      />

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: 16,
              }}
            >
              <div>
                <Title level={3}>Conversion Funnel Analysis</Title>
                <Text type="secondary">Multi-step funnel with time-to-convert metrics (P50/P90 percentiles)</Text>
              </div>
              <Space direction="vertical" align="end">
                <Text strong>Final Conversion Step:</Text>
                <Select value={selectedEndStep} onChange={setSelectedEndStep} style={{ width: 200 }}>
                  <Option value="credit_card">Entered Credit Card</Option>
                  <Option value="watched_movie">Watched Movie</Option>
                </Select>
              </Space>
            </div>

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic
                  title="Total Users Analyzed"
                  value={funnelData.summary.total_users_analyzed}
                  valueStyle={{ color: "#1890ff" }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Total Events Analyzed"
                  value={funnelData.summary.total_events_analyzed}
                  valueStyle={{ color: "#52c41a" }}
                />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}>
          <Card title="Funnel Visualization">
            <ResponsiveContainer width="100%" height={300}>
              <FunnelChart>
                <RechartsTooltip content={<CustomFunnelTooltip />} />
                <Funnel dataKey="value" data={getFunnelChartData()} isAnimationActive>
                  <LabelList position="center" fill="#fff" stroke="none" fontSize={14} />
                </Funnel>
              </FunnelChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}>
          <Card title="Conversion Rates by Step">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={getConversionBarData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="step" height={80} />
                <YAxis
                  label={{
                    value: "Conversion Rate (%)",
                    angle: -90,
                    position: "insideLeft",
                  }}
                />
                <RechartsTooltip content={<CustomBarTooltip />} />
                <Bar dataKey="rate" name="Conversion Rate">
                  {getConversionBarData().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        {funnelData.steps.map((step, index) => (
          <Col span={6} key={step.step_index}>
            <Card size="small">
              <Title level={5}>{step.step.replace("user ", "")}</Title>
              <Row gutter={[8, 8]}>
                <Col span={24}>
                  <Statistic
                    title="Users"
                    value={step.users_converted}
                    valueStyle={{
                      fontSize: "18px",
                      color: COLORS[index % COLORS.length],
                    }}
                  />
                </Col>
                <Col span={24}>
                  <Statistic
                    title="Conversion Rate"
                    value={step.conversion_rate}
                    suffix="%"
                    precision={2}
                    valueStyle={{ fontSize: "16px" }}
                  />
                </Col>
              </Row>

              {Object.keys(step.timing_stats).length > 0 && (
                <>
                  <Divider style={{ margin: "12px 0" }} />
                  <div>
                    <Text strong>Time to Convert:</Text>
                    <br />
                    <Row gutter={[4, 4]} style={{ marginTop: 8 }}>
                      <Col span={8}>
                        <Tooltip title="50th percentile - median time">
                          <Tag color="blue">P50: {formatDuration(step.timing_stats.median_minutes)}</Tag>
                        </Tooltip>
                      </Col>
                      <Col span={8}>
                        <Tooltip title="90th percentile - 90% of users convert within this time">
                          <Tag color="orange">P90: {formatDuration(step.timing_stats.p90_minutes)}</Tag>
                        </Tooltip>
                      </Col>
                      <Col span={8}>
                        <Tag color="gray">n={step.timing_stats.sample_size}</Tag>
                      </Col>
                    </Row>
                  </div>
                </>
              )}
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default FunnelAnalysis;
